import { useState } from 'react'
import type { CSSProperties, FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { ApiError, checkToken, setToken } from '@/app/lib/api'
import { Button } from '@/ui/Button'
import { FieldRow } from '@/ui/FieldRow'

type UnlockState = { status: 'idle' } | { status: 'checking' } | { status: 'failed'; message: string }

/** Where to go after unlocking: the page the visitor came from, never back to /unlock. */
function destination(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('/unlock') ? from : '/strategies'
}

function failureMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'unauthorized') return 'That access token was not accepted. Check it and try again.'
    if (error.code === 'network_error') return 'The server could not be reached. Try again in a moment.'
    return error.message
  }
  return 'Something went wrong while checking the token.'
}

/** The access-token prompt, shown only when the server answers 401. */
export default function Unlock() {
  const [token, setTokenValue] = useState('')
  const [state, setState] = useState<UnlockState>({ status: 'idle' })
  const navigate = useNavigate()
  const location = useLocation()

  async function submit(event: FormEvent) {
    event.preventDefault()
    const value = token.trim()
    if (!value) {
      setState({ status: 'failed', message: 'Enter the access token.' })
      return
    }
    setState({ status: 'checking' })
    try {
      await checkToken(value)
    } catch (error) {
      setState({ status: 'failed', message: failureMessage(error) })
      return
    }
    setToken(value)
    void navigate(destination(location.state), { replace: true })
  }

  return (
    <main
      data-quant-root
      style={{ '--h': 193 } as CSSProperties}
      className="fixed inset-0 flex flex-col overflow-y-auto bg-ink text-paper"
    >
      <div className="flex flex-1 items-center justify-center p-6">
        <form
          onSubmit={(event) => void submit(event)}
          className="w-full max-w-md border border-paper-35 bg-ink p-6"
        >
          <p className="font-sans text-[11px] font-bold tracking-[0.22em] text-accent-line uppercase">
            Quant Mission Control
          </p>
          <h1 className="mt-1 font-sans text-[18px] leading-tight font-bold tracking-[0.12em] uppercase">Unlock</h1>
          <p className="mt-3 font-mono text-[11px] leading-[1.5] text-paper-50">
            This server asks for an access token. It is kept in this browser only.
          </p>
          <div className="mt-4">
            <FieldRow label="Access token" htmlFor="access-token">
              <input
                id="access-token"
                type="password"
                autoComplete="off"
                autoFocus
                value={token}
                onChange={(event) => setTokenValue(event.target.value)}
                aria-invalid={state.status === 'failed'}
                aria-describedby={state.status === 'failed' ? 'unlock-error' : undefined}
                className="w-full min-w-0 border border-paper-35 bg-transparent px-[9px] py-[7px] font-mono text-[12.5px] text-paper outline-none focus:border-accent-fill"
              />
            </FieldRow>
          </div>
          {state.status === 'failed' && (
            <p id="unlock-error" role="alert" className="mt-3 font-mono text-[11px] text-alert">
              {state.message}
            </p>
          )}
          <div className="mt-4 flex justify-end">
            <Button type="submit" disabled={state.status === 'checking'}>
              {state.status === 'checking' ? 'Checking…' : 'Unlock'}
            </Button>
          </div>
        </form>
      </div>
      <footer className="shrink-0 px-6 py-3 text-center font-mono text-[10px] tracking-[0.1em] text-paper-50">
        Charts by{' '}
        <a href="https://www.tradingview.com/" target="_blank" rel="noreferrer" className="underline">
          TradingView Lightweight Charts
        </a>{' '}
        (Apache License 2.0)
      </footer>
    </main>
  )
}
